import type { TFunction } from 'i18next';

import type { FilterDef, FilterOption } from '@nucleus-commerce/admin-kit/index-table';
import { ExportKind, ImportKind, JobStatus } from '@nucleus-commerce/api-client/admin';

import type { JobTab } from '../schemas/imports-exports-search';

/** Fields GET /exports and GET /imports sort on; anything else is a 400. */
export const JOB_SORTS = ['createdAt'] as const;

const valuesOf = (values: Record<string, string>): FilterOption[] =>
  Object.values(values).map((value) => ({ value, label: value }));

const fieldsFor = (kinds: Record<string, string>) =>
  [
    { key: 'kind', type: 'enum', options: valuesOf(kinds) },
    { key: 'status', type: 'enum', options: valuesOf(JobStatus) },
    { key: 'createdAt', type: 'date-range' },
  ] as const;

/**
 * The filters as the URL and the API spell them, per tab. Both lists take `kind`, but each
 * offers only its own kinds, so the loader drops an export kind on the imports tab the same
 * way the page does and the two share a query key.
 */
export const JOB_FILTER_FIELDS = {
  exports: fieldsFor(ExportKind),
  imports: fieldsFor(ImportKind),
} as const satisfies Record<JobTab, unknown>;

export interface JobFiltersOptions {
  /** The `importsExports` namespace. */
  t: TFunction;
  tab: JobTab;
}

/** Filters of one tab's job table, in the order the filter menu lists them. */
export function jobFilters<T>({ t, tab }: JobFiltersOptions): Array<FilterDef<T>> {
  const kinds = tab === 'exports' ? ExportKind : ImportKind;
  return [
    {
      key: 'kind',
      label: t('filters.kind'),
      type: 'enum',
      pinned: true,
      options: valuesOf(kinds).map(({ value }) => ({ value, label: t(`kinds.${value}`) })),
    },
    {
      key: 'status',
      label: t('filters.status'),
      type: 'enum',
      pinned: true,
      options: valuesOf(JobStatus).map(({ value }) => ({ value, label: t(`status.${value}`) })),
    },
    { key: 'createdAt', label: t('filters.createdAt'), type: 'date-range', aliases: ['created'] },
  ];
}
